// src/modules/ml/EvaluationLab.jsx
// Model Evaluation Lab — standalone ML sidebar module
// Bias-Variance · Learning Curve · Cross-Validation · Permutation Importance · Metrics Reference

import React, { useState, useRef } from 'react';
import {
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import {
  parseCSVForML, inferTypes, multiLinearRegression, biasVariance,
  learningCurve, permutationImportance, trainTestSplit, kFoldCV, r2Score,
  generateMockDataset, parseNum, mean, std,
} from '../../utils/mlProcessing.js';
import { Panel, Metric, Tag, InfoBox, LogPanel, timestamp } from '../../components/ui.jsx';

const P = ['var(--orange)','var(--cyan)','var(--green)','var(--violet)','var(--gold)','var(--red)'];
const Sel = ({value,onChange,options,label,style}) => (<div style={style}>{label&&<div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'4px'}}>{label}</div>}<select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'7px 8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}>{options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}</select></div>);
const Btn = ({onClick,children,color='var(--orange)',disabled,style}) => (<button onClick={onClick} disabled={disabled} style={{padding:'9px 18px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,...style}}>{children}</button>);

const TABS = ['upload','setup','biasvariance','learningcurve','crossval','permutation','metrics'];

export default function EvaluationLab() {
  const [tab,setTab] = useState('upload');
  const [data,setData] = useState(null);
  const [logs,setLogs] = useState([{ts:'00:00:00',msg:'Evaluation Lab — upload data or choose a sample.',t:'info'}]);
  const [xCols,setXCols] = useState([]);
  const [yCol,setYCol] = useState('');
  const [bvData,setBvData] = useState([]);
  const [lcData,setLcData] = useState([]);
  const [cvData,setCvData] = useState(null);
  const [permData,setPermData] = useState([]);
  const [running,setRunning] = useState(false);
  const fileRef = useRef();
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const loadData = (text,name,direct) => {
    const parsed = direct||(text?parseCSVForML(text):null);
    if(!parsed){log('Parse error','err');return;}
    const types = inferTypes(parsed.headers,parsed.rows);
    const nc = types.filter(t=>t.type==='numeric').map(t=>t.col);
    setData({...parsed,types});
    setXCols(nc.slice(0,-1));
    setYCol(nc[nc.length-1]||nc[0]||'');
    log(`✓ "${name}": ${parsed.rows.length}×${parsed.headers.length}`,'ok');
    setTab('setup');
  };

  const rows = data?.rows||[], types = data?.types||[];
  const nc = types.filter(t=>t.type==='numeric').map(t=>t.col);
  const run = async(label,fn) => {setRunning(true);log(`Running ${label}...`);await new Promise(r=>setTimeout(r,50));fn();setRunning(false);};

  const FeatureSelect = ({color='var(--orange)'}) => (
    <div style={{marginBottom:'12px'}}>
      <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>FEATURE COLUMNS</div>
      <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'10px'}}>
        {nc.filter(c=>c!==yCol).map(col=>(
          <button key={col} onClick={()=>setXCols(s=>s.includes(col)?s.filter(c=>c!==col):[...s,col])}
            style={{padding:'4px 10px',border:`1px solid ${xCols.includes(col)?color:'var(--b0)'}`,background:xCols.includes(col)?color+'15':'var(--raised)',color:xCols.includes(col)?color:'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>
            {col}
          </button>
        ))}
      </div>
      <Sel value={yCol} onChange={setYCol} options={nc} label="TARGET (Y)" style={{maxWidth:'200px'}}/>
    </div>
  );

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?'var(--orange)':'transparent'}`,background:tab===t?'var(--orange)18':'transparent',color:tab===t?'var(--orange)':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* UPLOAD */}
      {tab==='upload'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
          <div onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f){const r=new FileReader();r.onload=ev=>loadData(ev.target.result,f.name);r.readAsText(f);}}} onDragOver={e=>e.preventDefault()} onClick={()=>fileRef.current?.click()} style={{border:'2px dashed var(--b1)',borderRadius:'4px',padding:'52px 24px',textAlign:'center',cursor:'pointer',background:'var(--surface)'}}>
            <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}} onChange={e=>{const f=e.target.files[0];if(f){const r=new FileReader();r.onload=ev=>loadData(ev.target.result,f.name);r.readAsText(f);}}}/>
            <div style={{fontSize:'40px',opacity:.3,marginBottom:'14px'}}>◉</div>
            <div style={{fontFamily:'Syne',fontWeight:700,fontSize:'18px',marginBottom:'7px'}}>Drop CSV File</div>
            <div style={{fontSize:'12px',color:'var(--t3)'}}>Numeric features + numeric target for evaluation</div>
          </div>
          <Panel title="SAMPLE DATASETS" accent="var(--orange)">
            {[['Housing Prices','housing','rooms,sqft,age,dist → price'],['Linear Dataset','linear','x1,x2 → y']].map(([name,type,desc])=>(
              <div key={type} onClick={()=>loadData(null,name,generateMockDataset(type))} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px',border:'1px solid var(--b0)',borderRadius:'3px',marginBottom:'6px',cursor:'pointer',background:'var(--raised)'}} onMouseEnter={e=>e.currentTarget.style.borderColor='var(--orange)'} onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b0)'}>
                <span style={{fontSize:'18px',color:'var(--orange)'}}>◉</span>
                <div style={{flex:1}}><div style={{fontSize:'12px',fontWeight:600}}>{name}</div><div style={{fontSize:'10px',color:'var(--t3)'}}>{desc}</div></div>
                <span style={{color:'var(--orange)'}}>→</span>
              </div>
            ))}
            <LogPanel logs={logs} height={70}/>
          </Panel>
        </div>
      )}

      {/* SETUP */}
      {tab==='setup'&&data&&(
        <Panel title="MODEL SETUP" accent="var(--orange)">
          <FeatureSelect/>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px',marginTop:'12px'}}>
            <Metric label="Training Features" value={xCols.length} color="var(--orange)"/>
            <Metric label="Total Rows" value={rows.length} color="var(--cyan)"/>
            <Metric label="Target" value={yCol||'not set'} color="var(--green)"/>
          </div>
          <InfoBox color="var(--t3)" icon="◉" style={{marginTop:'10px'}}>
            This lab evaluates model quality, not just training accuracy. All analyses use multiple train/test splits or cross-validation to get reliable estimates.
          </InfoBox>
        </Panel>
      )}

      {/* BIAS-VARIANCE */}
      {tab==='biasvariance'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="BIAS-VARIANCE TRADEOFF ANALYSIS" accent="var(--orange)">
            <div style={{marginBottom:'12px',fontSize:'11px',color:'var(--t3)'}}>
              Runs <strong style={{color:'var(--orange)'}}>10 bootstrap trials</strong> at 5 complexity levels (training epochs).
              At each complexity level, we measure average train/test R² across trials.<br/><br/>
              <strong style={{color:'var(--red)'}}>Bias²</strong> = 1 − avg test R² (how wrong on average)<br/>
              <strong style={{color:'var(--violet)'}}>Variance</strong> = spread of test R² across trials (how unstable)<br/>
              <strong style={{color:'var(--cyan)'}}>Total error</strong> = Bias² + Variance + irreducible noise
            </div>
            <FeatureSelect color="var(--orange)"/>
            <Btn onClick={()=>run('Bias-Variance (10×5)',()=>{
              if(!xCols.length){log('Select features','err');return;}
              const bv=biasVariance(rows,xCols,yCol,10);
              setBvData(bv);
              const overfit=bv.find(d=>d.avgTrainR2-d.avgTestR2>0.15);
              log(`Done. Overfitting appears around: ${overfit?`epochs=${overfit.epochs}`:'not detected in this range'}`,'ok');
            })} disabled={running}>{running?'ANALYZING 10×5 TRIALS...':'RUN BIAS-VARIANCE ANALYSIS →'}</Btn>
          </Panel>

          {bvData.length>0&&(
            <>
              <Panel title="TRAIN vs TEST R² ACROSS COMPLEXITY" accent="var(--orange)">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={bvData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="epochs" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'Training Epochs (model complexity →)',position:'insideBottom',offset:-5,fill:'var(--t3)',fontSize:9}}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}} domain={['auto',1]}/>
                    <Tooltip/>
                    <Legend/>
                    <Line type="monotone" dataKey="avgTrainR2" stroke="var(--cyan)" strokeWidth={2.5} dot={{r:5}} name="Avg Train R² (bias indicator)"/>
                    <Line type="monotone" dataKey="avgTestR2" stroke="var(--orange)" strokeWidth={2.5} dot={{r:5}} name="Avg Test R² (generalization)" strokeDasharray="5 3"/>
                  </LineChart>
                </ResponsiveContainer>
              </Panel>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <Panel title="BIAS (1 − Avg Test R²) — underfitting signal" accent="var(--red)">
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={bvData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="epochs" stroke="var(--t3)" tick={{fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}} domain={[0,'auto']}/>
                      <Tooltip/>
                      <Bar dataKey="bias" fill="var(--red)" opacity={.75} radius={[2,2,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                  <InfoBox color="var(--t3)" icon="◈">High bias = model too simple. Fix: more features, higher complexity, polynomial terms.</InfoBox>
                </Panel>
                <Panel title="VARIANCE — overfitting signal" accent="var(--violet)">
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={bvData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="epochs" stroke="var(--t3)" tick={{fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Bar dataKey="variance" fill="var(--violet)" opacity={.75} radius={[2,2,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                  <InfoBox color="var(--t3)" icon="◈">High variance = model too sensitive to training data. Fix: regularization, more data, fewer features.</InfoBox>
                </Panel>
              </div>
              <Panel title="COMPLEXITY TABLE" accent="var(--orange)">
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                  <thead><tr style={{background:'var(--deep)'}}>
                    {['Complexity','Epochs','Avg Train R²','Avg Test R²','Bias²','Variance','Zone'].map(h=><th key={h} style={{padding:'6px 10px',textAlign:'left',color:'var(--t3)',fontSize:'9px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                  </tr></thead>
                  <tbody>{bvData.map((d,i)=>{
                    const gap=d.avgTrainR2-d.avgTestR2;
                    const zone=d.bias>0.5?'Underfitting':gap>0.15?'Overfitting':'Sweet Spot';
                    return(<tr key={i} style={{borderBottom:'1px solid var(--b0)',background:zone==='Sweet Spot'?'var(--ggreen)':'transparent'}}>
                      <td style={{padding:'6px 10px',color:'var(--t3)'}}>{d.complexity}</td>
                      <td style={{padding:'6px 10px',color:'var(--t2)',fontFamily:'JetBrains Mono'}}>{d.epochs}</td>
                      <td style={{padding:'6px 10px',color:'var(--cyan)'}}>{d.avgTrainR2}</td>
                      <td style={{padding:'6px 10px',color:'var(--orange)'}}>{d.avgTestR2}</td>
                      <td style={{padding:'6px 10px',color:'var(--red)'}}>{d.bias}</td>
                      <td style={{padding:'6px 10px',color:'var(--violet)'}}>{d.variance}</td>
                      <td style={{padding:'6px 10px'}}><Tag color={zone==='Sweet Spot'?'var(--green)':zone==='Overfitting'?'var(--orange)':'var(--red)'}>{zone}</Tag></td>
                    </tr>);
                  })}</tbody>
                </table>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* LEARNING CURVE */}
      {tab==='learningcurve'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="LEARNING CURVE — How Much Data Do You Need?" accent="var(--cyan)">
            <div style={{marginBottom:'12px',fontSize:'11px',color:'var(--t3)'}}>
              Trains the model on 20%, 40%, 60%, 80%, and 100% of data with 20% holdout test each time.
              Shows whether performance is limited by <strong style={{color:'var(--cyan)'}}>data quantity</strong> or <strong style={{color:'var(--orange)'}}>model complexity</strong>.
            </div>
            <FeatureSelect color="var(--cyan)"/>
            <Btn onClick={()=>run('Learning Curve',()=>{
              if(!xCols.length){log('Select features','err');return;}
              const lc=learningCurve(rows,xCols,yCol);
              setLcData(lc);
              const last=lc[lc.length-1];
              const improving=lc.length>2&&lc[lc.length-1].testR2>lc[lc.length-3].testR2;
              log(`Done. Final test R²=${last?.testR2}. ${improving?'Still improving — more data would help.':'Plateau reached.'}`,'ok');
            })} color="var(--cyan)" disabled={running}>{running?'COMPUTING...':'COMPUTE LEARNING CURVE →'}</Btn>
          </Panel>

          {lcData.length>0&&(
            <>
              <Panel title="TRAIN vs VALIDATION R² as Dataset Grows" accent="var(--cyan)">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={lcData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="trainSize" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'Training Set Size →',position:'insideBottom',offset:-5,fill:'var(--t3)',fontSize:9}}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}} domain={['auto',1]}/>
                    <Tooltip/>
                    <Legend/>
                    <Line type="monotone" dataKey="trainR2" stroke="var(--cyan)" strokeWidth={2.5} dot={{fill:'var(--cyan)',r:5}} name="Train R²"/>
                    <Line type="monotone" dataKey="testR2" stroke="var(--orange)" strokeWidth={2.5} dot={{fill:'var(--orange)',r:5}} name="Test R²" strokeDasharray="5 3"/>
                  </LineChart>
                </ResponsiveContainer>
              </Panel>
              <Panel title="RMSE TREND" accent="var(--gold)">
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={lcData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="trainSize" stroke="var(--t3)" tick={{fontSize:9}}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                    <Tooltip/>
                    <Line type="monotone" dataKey="rmse" stroke="var(--gold)" strokeWidth={2} dot={{r:4}} name="Test RMSE"/>
                  </LineChart>
                </ResponsiveContainer>
              </Panel>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
                <InfoBox color="var(--red)" icon="⬟"><strong>Both curves low:</strong> Underfitting. Add features, polynomial terms, or reduce regularization.</InfoBox>
                <InfoBox color="var(--orange)" icon="⬟"><strong>Large gap:</strong> Overfitting. Add more data, regularize, or remove features.</InfoBox>
                <InfoBox color="var(--green)" icon="⬟"><strong>Both high + converging:</strong> Good generalization. Model is well-tuned.</InfoBox>
              </div>
            </>
          )}
        </div>
      )}

      {/* CROSS-VALIDATION */}
      {tab==='crossval'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="k-FOLD CROSS-VALIDATION" accent="var(--green)">
            <div style={{marginBottom:'12px',fontSize:'11px',color:'var(--t3)'}}>
              Splits data into k folds. Trains on k−1 folds, validates on the remaining fold, rotates k times.
              Gives a more reliable performance estimate than a single train/test split. 
              Reduces evaluation variance — especially important with small datasets.
            </div>
            <FeatureSelect color="var(--green)"/>
            <Btn onClick={()=>run('5-Fold CV',()=>{
              if(!xCols.length){log('Select features','err');return;}
              const foldSize=Math.floor(rows.length/5);
              const results=Array.from({length:5},(_,i)=>{
                const test=rows.slice(i*foldSize,(i+1)*foldSize);
                const train=[...rows.slice(0,i*foldSize),...rows.slice((i+1)*foldSize)];
                const m=multiLinearRegression(train,xCols,yCol,{lr:0.01,epochs:500});
                if(!m)return null;
                const testPreds=test.map(r=>{const x=xCols.map(c=>parseNum(r[c]));return x.reduce((s,v,j)=>s+v*m.weights[j],m.bias);});
                const testActuals=test.map(r=>parseNum(r[yCol])).filter(v=>!isNaN(v));
                const testR2=r2Score(testActuals.slice(0,testPreds.length),testPreds);
                return{fold:i+1,trainR2:m.r2,testR2,rmse:m.rmse,trainSize:train.length,testSize:test.length};
              }).filter(Boolean);
              const avgR2=mean(results.map(r=>r.testR2));
              const stdR2=std(results.map(r=>r.testR2));
              setCvData({results,avgR2:+avgR2.toFixed(4),stdR2:+stdR2.toFixed(4)});
              log(`5-Fold CV: avg test R²=${avgR2.toFixed(4)} ± ${stdR2.toFixed(4)}`,'ok');
            })} color="var(--green)" disabled={running}>{running?'RUNNING 5 FOLDS...':'RUN 5-FOLD CROSS-VALIDATION →'}</Btn>
          </Panel>

          {cvData&&(
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
                <Metric label="Avg Test R²" value={cvData.avgR2} color={cvData.avgR2>0.8?'var(--green)':cvData.avgR2>0.5?'var(--gold)':'var(--red)'} sub="mean across 5 folds"/>
                <Metric label="Std Dev R²" value={cvData.stdR2} color={cvData.stdR2<0.05?'var(--green)':'var(--orange)'} sub="lower = more stable"/>
                <Metric label="Best Fold R²" value={Math.max(...cvData.results.map(r=>r.testR2)).toFixed(4)} color="var(--cyan)"/>
                <Metric label="Worst Fold R²" value={Math.min(...cvData.results.map(r=>r.testR2)).toFixed(4)} color="var(--gold)"/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <Panel title="FOLD-BY-FOLD R² COMPARISON" accent="var(--green)">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={cvData.results}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="fold" stroke="var(--t3)" tick={{fontSize:9}} tickFormatter={v=>`Fold ${v}`}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}} domain={[0,1]}/>
                      <Tooltip/>
                      <Legend/>
                      <ReferenceLine y={cvData.avgR2} stroke="var(--gold)" strokeDasharray="5 3" label={{value:`avg=${cvData.avgR2}`,fill:'var(--gold)',fontSize:9}}/>
                      <Bar dataKey="trainR2" fill="var(--cyan)" opacity={.75} radius={[2,2,0,0]} name="Train R²"/>
                      <Bar dataKey="testR2" fill="var(--green)" opacity={.75} radius={[2,2,0,0]} name="Test R²"/>
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>
                <Panel title="FOLD DETAILS TABLE" accent="var(--green)">
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                    <thead><tr style={{background:'var(--deep)'}}>
                      {['Fold','Train R²','Test R²','RMSE','Train n','Test n'].map(h=><th key={h} style={{padding:'6px 8px',textAlign:'left',color:'var(--t3)',fontSize:'9px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                    </tr></thead>
                    <tbody>{cvData.results.map((r,i)=>(
                      <tr key={i} style={{borderBottom:'1px solid var(--b0)'}}>
                        <td style={{padding:'6px 8px',color:'var(--green)',fontWeight:600}}>#{r.fold}</td>
                        <td style={{padding:'6px 8px',color:'var(--cyan)'}}>{r.trainR2}</td>
                        <td style={{padding:'6px 8px',color:'var(--green)'}}>{r.testR2.toFixed(4)}</td>
                        <td style={{padding:'6px 8px',color:'var(--gold)'}}>{r.rmse}</td>
                        <td style={{padding:'6px 8px',color:'var(--t3)'}}>{r.trainSize}</td>
                        <td style={{padding:'6px 8px',color:'var(--t3)'}}>{r.testSize}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                  <InfoBox color="var(--t3)" icon="◈" style={{marginTop:'8px'}}>
                    High variance across folds (large std) → unstable model. Consider more data or simpler model.
                  </InfoBox>
                </Panel>
              </div>
            </>
          )}
        </div>
      )}

      {/* PERMUTATION IMPORTANCE */}
      {tab==='permutation'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="PERMUTATION FEATURE IMPORTANCE" accent="var(--violet)">
            <div style={{marginBottom:'12px',fontSize:'11px',color:'var(--t3)'}}>
              Trains a model, then randomly shuffles one feature at a time and measures the R² drop.
              <strong style={{color:'var(--violet)'}}> Large drop = that feature is critical.</strong> Small/no drop = redundant or irrelevant.
              This method is <strong>model-agnostic</strong> — works for any regression model.
            </div>
            <FeatureSelect color="var(--violet)"/>
            <Btn onClick={()=>run('Permutation Importance',()=>{
              if(!xCols.length){log('Select features','err');return;}
              const base=multiLinearRegression(rows,xCols,yCol,{lr:0.01,epochs:500});
              if(!base){log('Baseline training failed','err');return;}
              const perm=permutationImportance(rows,xCols,yCol,base.r2);
              setPermData(perm);
              log(`Baseline R²=${base.r2}. Most important: ${perm[0]?.col} (R² drop=${perm[0]?.importance})`,'ok');
            })} color="var(--violet)" disabled={running}>{running?'COMPUTING...':'COMPUTE PERMUTATION IMPORTANCE →'}</Btn>
          </Panel>

          {permData.length>0&&(
            <>
              <Panel title="FEATURE IMPORTANCE — R² DROP WHEN SHUFFLED" badge="higher = more important" accent="var(--violet)">
                <ResponsiveContainer width="100%" height={Math.max(180,permData.length*44+40)}>
                  <BarChart data={permData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false}/>
                    <XAxis type="number" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'R² Drop (importance)',position:'insideBottom',offset:-5,fill:'var(--t3)',fontSize:9}}/>
                    <YAxis type="category" dataKey="col" stroke="var(--t3)" tick={{fontSize:9}} width={90}/>
                    <Tooltip formatter={v=>[v,'R² Drop']}/>
                    <Bar dataKey="importance" radius={[0,2,2,0]}>{permData.map((d,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
              <Panel title="RANKING TABLE" accent="var(--violet)">
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                  <thead><tr style={{background:'var(--deep)'}}>
                    {['#','Feature','R² Drop','R² Without Feature','Impact'].map(h=><th key={h} style={{padding:'7px 12px',textAlign:'left',color:'var(--t3)',fontSize:'10px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                  </tr></thead>
                  <tbody>{permData.map((d,i)=>(
                    <tr key={d.col} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,0,0,.08)'}}>
                      <td style={{padding:'7px 12px',color:'var(--t3)'}}>#{i+1}</td>
                      <td style={{padding:'7px 12px',color:P[i%P.length],fontWeight:600}}>{d.col}</td>
                      <td style={{padding:'7px 12px',fontFamily:'JetBrains Mono',color:'var(--violet)'}}>{d.importance}</td>
                      <td style={{padding:'7px 12px',fontFamily:'JetBrains Mono',color:'var(--t2)'}}>{d.r2Without.toFixed(4)}</td>
                      <td style={{padding:'7px 12px'}}><Tag color={d.importance>0.1?'var(--green)':d.importance>0.01?'var(--gold)':'var(--t3)'}>{d.importance>0.1?'★ Critical':d.importance>0.01?'◈ Useful':'◎ Weak'}</Tag></td>
                    </tr>
                  ))}</tbody>
                </table>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* METRICS REFERENCE */}
      {tab==='metrics'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {[
            {title:'REGRESSION METRICS',color:'var(--green)',rows:[
              ['R²','0..1','Proportion of variance explained. 1.0 = perfect. Negative = worse than predicting the mean.','High (>0.9) for physics models. >0.7 for social data is decent.'],
              ['Adjusted R²','0..1','R² penalized for number of features. Increases only if new feature actually improves model.','Always use over R² for multiple regression.'],
              ['RMSE','≥0','Root Mean Squared Error. Same units as target. Penalizes large errors heavily (squared).','Business-interpretable: "on average, off by X units."'],
              ['MAE','≥0','Mean Absolute Error. Average of |yᵢ−ŷᵢ|. More robust to outliers than RMSE.','Preferred when outliers exist or you want equal error weighting.'],
              ['MSE','≥0','Mean Squared Error. Loss function for training. Overstates error due to squaring.','Use for training loss; report RMSE to stakeholders.'],
              ['MAPE','0..100%','Mean Absolute Percentage Error. Scale-independent.','Fails when actuals near zero. Good for business forecasting.'],
            ]},
            {title:'CLASSIFICATION METRICS',color:'var(--violet)',rows:[
              ['Accuracy','0..1','Fraction of correct predictions. Misleading for imbalanced classes.','Only valid if classes are balanced. Never use alone for medical/fraud tasks.'],
              ['Precision','0..1','TP/(TP+FP). "Of predicted positives, how many were actually positive?"','Prioritize when false positives are costly (spam filter, fraud alert).'],
              ['Recall (Sensitivity)','0..1','TP/(TP+FN). "Of actual positives, how many did the model catch?"','Prioritize when false negatives are costly (cancer detection, safety systems).'],
              ['F1 Score','0..1','Harmonic mean of precision & recall. Balances both without arithmetic averaging.','Default choice for imbalanced classification. Macro F1 for multiclass.'],
              ['AUC-ROC','0..1','Area under ROC curve (TPR vs FPR at all thresholds). Threshold-agnostic.','0.5 = random. >0.9 = excellent. Use when you tune the decision threshold.'],
              ['Macro F1','0..1','F1 averaged equally across all classes. Penalizes poor performance on any class.','Best metric for multiclass imbalanced data.'],
            ]},
            {title:'CLUSTERING METRICS',color:'var(--gold)',rows:[
              ['Inertia (WCSS)','≥0','Sum of squared distances to cluster centroid. Lower = tighter clusters.','Use relative to other k values. Not meaningful alone.'],
              ['Silhouette Score','−1..+1','(b−a)/max(a,b). a=intra-cluster dist, b=nearest-cluster dist.','&gt;0.5 = well-separated. &lt;0.25 = overlapping or wrong k.'],
              ['Davies-Bouldin','≥0','Avg max(Sᵢ+Sⱼ)/dᵢⱼ. Lower = better cluster separation.','Use alongside silhouette for validation.'],
              ['Calinski-Harabasz','≥0','Between-cluster variance / within-cluster variance. Higher = better.','Fast to compute. Good for convex clusters.'],
            ]},
            {title:'CROSS-VALIDATION & EVALUATION CONCEPTS',color:'var(--orange)',rows:[
              ['Holdout Split','Train/Test','Single 80/20 or 70/30 split. Fast but high variance on small datasets.','Use for large datasets (>10k rows).'],
              ['k-Fold CV','k=5 or 10','Train on k−1 folds, test on 1. Rotate k times, average results.','Gold standard for small/medium datasets.'],
              ['Stratified k-Fold','Balanced','k-Fold but each fold maintains original class proportions.','Always use for classification.'],
              ['Leave-One-Out (LOO)','k=n','Extreme case of k-fold. High compute, low bias.','Only for tiny datasets (<50 rows).'],
              ['Train/Val/Test','3-way split','Hold out a test set untouched. Use val for hyperparameter tuning.','Required for rigorous model selection.'],
            ]},
          ].map(({title,color,rows})=>(
            <Panel key={title} title={title} accent={color}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                <thead><tr style={{background:'var(--deep)'}}>
                  {['Metric','Range','Definition','When to use'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',color:'var(--t3)',fontSize:'9px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                </tr></thead>
                <tbody>{rows.map(([name,range,def,when],i)=>(
                  <tr key={name} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,0,0,.05)'}}>
                    <td style={{padding:'7px 10px',color,fontWeight:600,whiteSpace:'nowrap'}}>{name}</td>
                    <td style={{padding:'7px 10px',fontFamily:'JetBrains Mono',fontSize:'10px',color:'var(--t2)',whiteSpace:'nowrap'}}>{range}</td>
                    <td style={{padding:'7px 10px',color:'var(--t2)',maxWidth:'280px'}}>{def}</td>
                    <td style={{padding:'7px 10px',color:'var(--t3)',fontSize:'10px',maxWidth:'200px'}}>{when}</td>
                  </tr>
                ))}</tbody>
              </table>
            </Panel>
          ))}
        </div>
      )}

      {!['upload'].includes(tab)&&<LogPanel logs={logs} height={55}/>}
    </div>
  );
}
